// A kiadás-sor MUNKATÁRS cellája — helyben szerkeszthető, keresős választó.
//
// Miért a cellában: a besorolás-munka sorozatban megy (a NAV egy hónapnyi bizonylatot hoz egyszerre), és a
// kezelő ugyanabban a mozdulatban dönti el, melyik kategóriába, melyik hónapba ÉS kihez tartozik a sor. Egy
// modál megnyitása soronként két extra kattintás lenne.
//
// A NAV-soron is engedett: a munkatárs-kötés a MI besorolásunk, nem az adóhatóság rekordja.
//
// ⚠️ A CELLA NEM MENT — a választás a sor FÜGGŐ állapotát állítja, a mentést a sor végi „Mentés" gomb
// indítja. Ugyanaz a szerződés, mint a kategóriánál és a hónapnál: a három mező EGY kérésben megy ki.
//
// A VÁLASZTÓ CSAK BÉR TÍPUSÚ TÉTELNÉL áll (`allowed`): a sor kategóriáján ülő jelölés dönt. Máshol a mező
// üres jelzés — így a besorolás-munka közben nem kell minden soron eldönteni, hogy releváns-e.
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { Text } from '@/components/ui/text';
import { employeeSearch } from '@/features/employees/search';

import type { ExpenseRow } from '@atlas/contracts';
import type { JSX } from 'react';

export interface ExpenseEmployeeCellProps {
  row: ExpenseRow;
  /** A MEGJELENÍTENDŐ érték: a függő választás, ha van, különben a soré. */
  value: string | null;
  onChange: (identityId: string | null) => void;
  canManage: boolean;
  /**
   * Áll-e a választó ezen a soron: a sor (függő) kategóriája BÉR TÍPUSÚ TÉTEL, VAGY már van beállított
   * érték. A második ág szándékos: egy kategória-váltás nem rejtheti el a már megadott kötést —
   * az eltűnne szem elől, miközben a szerveren ott marad.
   */
  allowed: boolean;
}

export function ExpenseEmployeeCell({
  row,
  value,
  onChange,
  canManage,
  allowed,
}: ExpenseEmployeeCellProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // A már mentett kötés CÍMKÉJE a sorról jön — enélkül a választó üresen állna, amíg a keresés le nem fut
  // (a címtárat nem töltjük be előre, lásd `employeeSearch`).
  const initialOption =
    value !== null && value === row.employeeIdentityId && row.employeeName !== null
      ? { id: value, label: row.employeeName }
      : null;

  if (!allowed) {
    return (
      <Text variant="secondary" as="span" className="text-xs">
        {t('expenses.employeeNotApplicable')}
      </Text>
    );
  }

  if (!canManage) {
    return (
      <div className="min-w-0">
        {row.employeeIdentityId === null ? (
          <Text variant="secondary" as="span" className="text-xs">
            {t('expenses.employeeNone')}
          </Text>
        ) : (
          <span className="truncate">{row.employeeName ?? row.employeeIdentityId}</span>
        )}
      </div>
    );
  }

  return (
    <EntityCombobox
      value={value}
      onChange={onChange}
      search={employeeSearch(locale)}
      {...(initialOption ? { initialOption } : {})}
      placeholder={t('expenses.employeeNone')}
      aria-label={t('expenses.employeeAria', { supplier: row.supplierName })}
    />
  );
}
